import { SimpleGrid, Skeleton, Stat, StatLabel, StatNumber } from '@chakra-ui/react';

import { useGetCallHistoryStatsQuery } from '../../gql/generated/graphql';

import { CallHistoryFilterValues } from './CallHistoryFilters';
import { formatDuration } from './utils';

type CallStatsBarProps = {
  filters: CallHistoryFilterValues;
};

const formatPct = (numerator: number, denominator: number): string => {
  if (!denominator) {
    return '0%';
  }
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
};

const CallStatsBar = ({ filters }: CallStatsBarProps) => {
  const { data, loading } = useGetCallHistoryStatsQuery({
    variables: {
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      agentUserId: filters.agentUserId || undefined,
      outcome: filters.outcome || undefined,
      flowType: filters.flowType || undefined,
    },
  });

  const stats = data?.getCallHistoryStats;

  const statItems = [
    { label: 'Total Calls', value: stats?.totalCalls ?? '—' },
    {
      label: 'Answered %',
      value: loading ? '—' : formatPct(stats?.answeredCalls ?? 0, stats?.totalCalls ?? 0),
    },
    {
      label: 'Voicemail %',
      value: loading ? '—' : formatPct(stats?.voicemailCalls ?? 0, stats?.totalCalls ?? 0),
    },
    {
      label: 'SIP Failure %',
      value: loading ? '—' : formatPct(stats?.sipFailures ?? 0, stats?.totalCalls ?? 0),
    },
    { label: 'Avg Duration', value: loading ? '—' : formatDuration(stats?.avgDurationSeconds) },
  ];

  return (
    <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={5}>
      {statItems.map(({ label, value }) => (
        <Stat
          key={label}
          bg="white"
          p={4}
          borderRadius="lg"
          shadow="sm"
          border="1px solid"
          borderColor="gray.200"
        >
          <StatLabel color="gray.500" fontSize="xs">
            {label}
          </StatLabel>
          {loading ? (
            <Skeleton height="28px" mt={1} />
          ) : (
            <StatNumber fontSize="xl">{value}</StatNumber>
          )}
        </Stat>
      ))}
    </SimpleGrid>
  );
};

export default CallStatsBar;
